// ProposalLinksManager.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosDefault from 'axios';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  IconButton,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  MessageBar,
  MessageBarType,
  Spinner,
  TooltipHost,
  Dialog,
  DialogType,
  DialogFooter,
  TagPicker,
  useTheme,
  Label,
  Separator,
} from '@fluentui/react';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

const ProposalLinkManager = ({
  entityType = 'projects', // 'projects' or 'people'
  entityId,
  axiosInstance,
}) => {
  const axios = axiosInstance || axiosDefault;
  const theme = useTheme();

  const [linked, setLinked] = useState([]);             // current linked proposals
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);              // add/remove in progress
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [isManageOpen, setIsManageOpen] = useState(false);
  const [toAdd, setToAdd] = useState([]);               // TagPicker items (proposals to add)

  const searchAbortRef = useRef(null);
  const debounceRef = useRef(null);

  const routes = useMemo(() => {
    if (entityType === 'compass_projects') {
      return {
        list: `/api/compass_projects/${entityId}/proposals`,
        add: `/api/proposals/proposal_project_link`,
        remove: `/api/proposals/proposal_project_link`,
        foreignKey: 'project_id',
      };
    }
    return {
      list: `/api/people/${entityId}/proposals`,
      add: `/api/proposals/proposal_staff_link`,
      remove: `/api/proposals/proposal_staff_link`,
      foreignKey: 'person_id',
    };
  }, [entityType, entityId]);

  const clearNotice = () => {
    setError(null);
    setInfo(null);
  };

  const fetchLinked = useCallback(async () => {
    clearNotice();
    setLoading(true);
    try {
      // Expect API to return an array of proposals:
      // { id, title, proposal_number, client_name, status, ... }
      const { data } = await axios.get(routes.list);
      setLinked(Array.isArray(data) ? data : (data?.proposals || []));
    } catch (e) {
      setError(`Failed to load linked proposals: ${e?.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [axios, routes.list]);

  useEffect(() => {
    if (!entityId) return;
    fetchLinked();
  }, [entityId, fetchLinked]);

  // ------- TagPicker (search proposals to add) -------
  const proposalToPickerItem = (p) => ({
    key: p.id,
    name: p.title ? `${p.title} • ${p.proposal_number || '—'}` : (p.proposal_number || p.id),
    _raw: p,
  });

  const onFilterChanged = async (filterText) => {
    if (!filterText || !filterText.trim()) return [];
    // debounce to avoid hammering your API
    if (debounceRef.current) clearTimeout(debounceRef.current);

    return new Promise((resolve) => {
      debounceRef.current = setTimeout(async () => {
        try {
          if (searchAbortRef.current) {
            // axios can't truly abort without AbortController; if you wire it up, use it here
          }
          const { data } = await axios.get(`/api/proposals`, {
            params: { search: filterText, limit: 15 },
          });

          console.log({data});

          const items = (Array.isArray(data.data) ? data.data : data?.proposals || []).map(proposalToPickerItem);

          // Exclude already-linked proposals
          const linkedIds = new Set(linked.map((p) => p.id));
          resolve(items.filter((it) => !linkedIds.has(it.key) && !toAdd.some((sel) => sel.key === it.key)));
        } catch (e) {
          // swallow search errors to keep UX smooth
          resolve([]);
        }
      }, 250);
    });
  };

  const onItemSelected = (item) => {
    // Prevent selecting duplicates (TagPicker calls this before insert)
    if (linked.some((p) => p.id === item.key) || toAdd.some((t) => t.key === item.key)) {
      return null;
    }
    return item;
  };

  // ------- Add proposals -------
  const handleAdd = async () => {
    if (!toAdd.length) return;
    setBusy(true);
    clearNotice();
    try {
      const payloads = toAdd.map((t) => ({
        proposal_id: t.key,
        [routes.foreignKey]: entityId,
      }));

      // Optimistic UI
      const optimistic = [
        ...linked,
        ...toAdd.map((t) => t._raw || { id: t.key, title: t.name, proposal_number: '', status: '' }),
      ];
      setLinked(optimistic);

      // POST each link (or batch if your API supports it)
      await Promise.all(payloads.map((p) => axios.post(routes.add, p)));

      setInfo(`Added ${toAdd.length} proposal${toAdd.length > 1 ? 's' : ''}.`);
      setToAdd([]);
      // Refresh to stay source-of-truth
      await fetchLinked();
    } catch (e) {
      setError(`Failed to add: ${e?.response?.data?.message || e.message}`);
      // rollback by re-fetching
      await fetchLinked();
    } finally {
      setBusy(false);
    }
  };

  // ------- Remove link -------
  const handleRemove = async (proposal) => {
    setBusy(true);
    clearNotice();
    try {
      // Optimistic remove
      const prev = linked;
      setLinked((cur) => cur.filter((p) => p.id !== proposal.id));

      // If your API uses a composite body on DELETE:
      await axios.delete(routes.remove, {
        data: { proposal_id: proposal.id, [routes.foreignKey]: entityId },
      });

      setInfo(`Removed “${proposal.title || proposal.proposal_number || proposal.id}”.`);
    } catch (e) {
      setError(`Failed to remove: ${e?.response?.data?.message || e.message}`);
      // rollback
      await fetchLinked();
    } finally {
      setBusy(false);
    }
  };

  // ------- List columns -------
  const columns = useMemo(() => ([
    {
      key: 'col-title',
      name: 'Title',
      fieldName: 'title',
      minWidth: 220,
      // maxWidth: 420,
      isResizable: true,
      onRender: (item) => (
        <Link to={`/proposals/${item.id}`}>
          {item.title}
        </Link>
      ),
    },
    // {
    //   key: 'col-client',
    //   name: 'Client',
    //   fieldName: 'client_name',
    //   minWidth: 160,
    //   isResizable: true,
    //   onRender: (item) => <Text>{item.client_name || '—'}</Text>,
    // },
    // {
    //   key: 'col-status',
    //   name: 'Status',
    //   fieldName: 'status',
    //   minWidth: 120,
    //   maxWidth: 140,
    //   // onRender: (item) => <Text>{item.status || '—'}</Text>,
    // },
    {
      key: 'col-actions',
      name: 'Actions',
      minWidth: 90,
      maxWidth: 110,
      onRender: (item) => (
        <TooltipHost content="Remove link">
          <IconButton
            iconProps={{ iconName: 'Delete' }}
            title="Remove"
            ariaLabel="Remove"
            disabled={busy}
            onClick={() => handleRemove(item)}
          />
        </TooltipHost>
      ),
    },
  ]), [busy, theme.palette.neutralSecondary]);

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Stack>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Text variant='xLarge'>Linked Proposals</Text>
          <DefaultButton
            text="Manage"
            onClick={() => setIsManageOpen(true)}
          />
        </Stack>
        <Separator />
      </Stack>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}
      {info && (
        <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setInfo(null)}>
          {info}
        </MessageBar>
      )}

      {loading ? (
        <Stack horizontalAlign="center" styles={{ root: { padding: 24 } }}>
          <Spinner label="Loading linked proposals..." />
        </Stack>
      ) : (
        <DetailsList
          items={linked}
          columns={columns}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          compact
        />
      )}

      <Dialog
        hidden={!isManageOpen}
        onDismiss={() => setIsManageOpen(false)}
        minWidth={640}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Manage Proposal Links',
          subText:
            entityType === 'projects'
              ? 'Add or remove proposals linked to this project.'
              : 'Add or remove proposals linked to this person.',
        }}
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <Text variant="smallPlus">Search and select proposals to add</Text>
          <TagPicker
            onResolveSuggestions={onFilterChanged}
            onEmptyResolveSuggestions={() => []}
            getTextFromItem={(it) => it.name}
            pickerSuggestionsProps={{ suggestionsHeaderText: 'Proposals', noResultsFoundText: 'No matches' }}
            selectedItems={toAdd}
            onChange={setToAdd}
            onItemSelected={onItemSelected}
            inputProps={{ placeholder: 'Type to search proposals…' }}
          />

          <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 8 } }}>
            <PrimaryButton
              text={`Add ${toAdd.length ? `(${toAdd.length})` : ''}`}
              onClick={handleAdd}
              disabled={!toAdd.length || busy}
            />
            <DefaultButton
              text="Close"
              onClick={() => setIsManageOpen(false)}
            />
          </Stack>

          <Stack tokens={{ childrenGap: 6 }} styles={{ root: { marginTop: 16 } }}>
            <Text variant="smallPlus" styles={{ root: { color: theme.palette.neutralSecondary } }}>
              Currently linked
            </Text>
            <DetailsList
              items={linked}
              columns={columns}
              selectionMode={SelectionMode.none}
              layoutMode={DetailsListLayoutMode.justified}
              compact
            />
          </Stack>
        </Stack>
        <DialogFooter />
      </Dialog>
    </Stack>
  );
};

export default ProposalLinkManager;
